/**
 * Tiny time-formatting helpers for the public surface.
 *
 * Centralised so timeline / status / result / attempt views render
 * dates consistently, and so a future i18n pass has one chokepoint
 * to attack.
 */

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

/**
 * Human-readable countdown to a deadline. Returns:
 *   - "Expired" when target <= now
 *   - "Less than a minute" / "X minutes" / "X hours, Y minutes" / "X days, Y hours"
 *
 * Stops at the day granularity — we never show weeks/months because the
 * apply-portal token TTL is bounded to days and showing "in 3 weeks"
 * would be misleading (the BE always re-clamps to the per-purpose TTL).
 */
export function formatTimeUntil(iso: string | null | undefined): string {
  if (!iso) return "—";
  let target: number;
  try {
    target = new Date(iso).getTime();
    if (Number.isNaN(target)) return iso;
  } catch {
    return iso;
  }
  const now = Date.now();
  const diffMs = target - now;
  if (diffMs <= 0) return "Expired";

  const totalSeconds = Math.floor(diffMs / 1000);
  const totalMinutes = Math.floor(totalSeconds / 60);
  const totalHours = Math.floor(totalMinutes / 60);
  const totalDays = Math.floor(totalHours / 24);

  if (totalMinutes < 1) return "Less than a minute";
  if (totalMinutes < 60) return `${totalMinutes} minute${totalMinutes === 1 ? "" : "s"}`;
  if (totalHours < 24) {
    const minutes = totalMinutes % 60;
    return minutes === 0
      ? `${totalHours} hour${totalHours === 1 ? "" : "s"}`
      : `${totalHours} hour${totalHours === 1 ? "" : "s"}, ${minutes} minute${minutes === 1 ? "" : "s"}`;
  }
  const hours = totalHours % 24;
  return hours === 0
    ? `${totalDays} day${totalDays === 1 ? "" : "s"}`
    : `${totalDays} day${totalDays === 1 ? "" : "s"}, ${hours} hour${hours === 1 ? "" : "s"}`;
}
